import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Navigate } from "react-router-dom";
import { api, BACKEND_URL } from "../lib/api";
import { LogOut, Plus, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";

const tabs = ["episodes", "guests", "team", "products", "applications", "orders", "donations", "newsletter"];

export default function AdminDashboard() {
    const { user, loading, logout } = useAuth();
    const [tab, setTab] = useState("episodes");

    if (loading) return <div className="px-6 py-20 font-serif text-2xl">Loading…</div>;
    if (!user) return <Navigate to="/admin/login" replace />;

    return (
        <div data-testid="admin-dashboard" className="min-h-screen bg-[#FAFAFA]">
            <div className="border-b border-black/10 bg-white px-6 md:px-12 py-6 flex justify-between items-center">
                <div>
                    <div className="eyebrow text-[#B8860B]">Studio</div>
                    <h1 className="font-serif text-2xl tracking-tight">Admin Dashboard</h1>
                </div>
                <div className="flex items-center gap-4">
                    <span className="text-sm text-black/60 hidden md:inline">{user.email}</span>
                    <button data-testid="admin-logout" onClick={logout} className="flex items-center gap-2 text-sm hover:text-[#B8860B]">
                        <LogOut size={14} /> Logout
                    </button>
                </div>
            </div>

            <div className="px-6 md:px-12 py-6 flex gap-2 md:gap-6 border-b border-black/10 bg-white overflow-x-auto">
                {tabs.map((t) => (
                    <button
                        key={t}
                        data-testid={`admin-tab-${t}`}
                        onClick={() => setTab(t)}
                        className={`eyebrow text-[12px] py-2 px-1 border-b-2 transition-colors ${
                            tab === t ? "border-[#D4AF37] text-black" : "border-transparent text-black/50 hover:text-black"
                        }`}
                    >
                        {t}
                    </button>
                ))}
            </div>

            <div className="px-6 md:px-12 py-10">
                {tab === "episodes" && <EpisodesAdmin />}
                {tab === "guests" && <GuestsAdmin />}
                {tab === "team" && <TeamAdmin />}
                {tab === "products" && <ProductsAdmin />}
                {tab === "applications" && <ApplicationsAdmin />}
                {tab === "orders" && <OrdersAdmin />}
                {tab === "donations" && <DonationsAdmin />}
                {tab === "newsletter" && <NewsletterAdmin />}
            </div>
        </div>
    );
}

const FileUpload = ({ onUpload, label, accept }) => {
    const [uploading, setUploading] = useState(false);
    const handle = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            onUpload(data.url);
            toast.success("Uploaded");
        } catch (err) {
            toast.error("Upload failed");
        } finally {
            setUploading(false);
        }
    };
    return (
        <label className="inline-flex items-center gap-2 text-sm cursor-pointer text-[#B8860B] hover:underline">
            <Upload size={14} /> {uploading ? "Uploading…" : label}
            <input type="file" accept={accept} onChange={handle} className="hidden" />
        </label>
    );
};

const TextField = ({ label, value, onChange, type = "text", required, testid }) => (
    <div>
        <label className="block eyebrow text-black/60 mb-2 text-[10px]">{label}</label>
        <input
            type={type}
            value={value || ""}
            onChange={(e) => onChange(type === "number" ? parseFloat(e.target.value || 0) : e.target.value)}
            required={required}
            data-testid={testid}
            className="w-full border border-black/15 px-3 py-2 bg-white text-sm focus:border-[#D4AF37] outline-none"
        />
    </div>
);

const TextArea = ({ label, value, onChange, testid }) => (
    <div className="md:col-span-2">
        <label className="block eyebrow text-black/60 mb-2 text-[10px]">{label}</label>
        <textarea
            value={value || ""}
            rows={3}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testid}
            className="w-full border border-black/15 px-3 py-2 bg-white text-sm focus:border-[#D4AF37] outline-none"
        />
    </div>
);

function EpisodesAdmin() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const load = async () => {
        try {
            const r = await api.get("/episodes");
            setItems(r.data || []);
        } catch {
            toast.error("Could not load episodes from RSS feed");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const refresh = async () => {
        setRefreshing(true);
        try {
            const r = await api.post("/episodes/refresh");
            setItems(r.data.episodes || []);
            toast.success(`Synced ${r.data.count} episode(s) from RSS feed`);
        } catch {
            toast.error("Refresh failed");
        } finally {
            setRefreshing(false);
        }
    };

    return (
        <div data-testid="admin-episodes">
            <div className="bg-white border border-black/10 p-6 mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <div className="eyebrow text-[#B8860B] mb-2 text-[10px]">Source of Truth</div>
                    <h3 className="font-serif text-2xl">Episodes are synced from your podcast RSS feed</h3>
                    <p className="text-sm text-black/60 mt-2 max-w-2xl font-light">
                        Publish a new episode in Spotify for Podcasters (or wherever you publish). Within ~5 minutes the shop will reflect the latest list. Click <em>Refresh now</em> to bypass the cache.
                    </p>
                </div>
                <div className="flex gap-3 flex-shrink-0">
                    <button onClick={refresh} disabled={refreshing} data-testid="episodes-refresh" className="kind-btn-secondary">
                        {refreshing ? "Syncing…" : "Refresh now"}
                    </button>
                    <a href="https://podcasters.spotify.com/pod/dashboard/home" target="_blank" rel="noreferrer" data-testid="open-spotify-podcasters" className="kind-btn-gold">
                        Open Spotify →
                    </a>
                </div>
            </div>

            <h3 className="font-serif text-2xl mb-6">Episodes ({items.length})</h3>
            {loading ? (
                <p className="text-black/50">Loading…</p>
            ) : items.length === 0 ? (
                <div className="bg-white border border-black/10 p-10 text-center">
                    <p className="font-serif text-xl">No episodes found in your RSS feed.</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {items.map((ep) => (
                        <div key={ep.id} className="flex gap-4 items-center bg-white border border-black/10 p-4">
                            {ep.cover_image && <img src={ep.cover_image} alt={ep.title} className="w-14 h-14 object-cover flex-shrink-0" />}
                            <div className="flex-1 min-w-0">
                                <div className="font-serif text-lg truncate">{ep.title}</div>
                                <div className="text-xs text-black/50">
                                    Ep {ep.episode_number} · {ep.duration} {ep.release_date && `· ${ep.release_date}`}
                                </div>
                            </div>
                            {ep.external_url && (
                                <a href={ep.external_url} target="_blank" rel="noreferrer" className="eyebrow text-[10px] text-[#B8860B] hover:text-black flex-shrink-0">
                                    Open →
                                </a>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function GuestsAdmin() {
    const [items, setItems] = useState([]);
    const empty = { name: "", title: "", bio: "", photo_url: "", social_links: {} };
    const [form, setForm] = useState(empty);
    const load = () => api.get("/guests").then((r) => setItems(r.data || []));
    useEffect(() => { load(); }, []);
    const save = async () => {
        if (!form.name.trim() || !form.bio.trim()) {
            toast.error("Name and bio are required");
            return;
        }
        try { await api.post("/guests", form); toast.success("Guest added"); setForm(empty); load(); } catch { toast.error("Failed"); }
    };
    const del = async (id) => { await api.delete(`/guests/${id}`); load(); };

    return (
        <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
                <h3 className="font-serif text-2xl mb-6">New Guest</h3>
                <div className="grid md:grid-cols-2 gap-4 bg-white p-6 border border-black/10">
                    <TextField label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="gu-name" />
                    <TextField label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="gu-title" />
                    <TextArea label="Bio" value={form.bio} onChange={(v) => setForm({ ...form, bio: v })} testid="gu-bio" />
                    <div className="md:col-span-2">
                        <TextField label="Photo URL" value={form.photo_url} onChange={(v) => setForm({ ...form, photo_url: v })} testid="gu-photo" />
                        <FileUpload accept="image/*" label="Upload photo" onUpload={(url) => setForm({ ...form, photo_url: `${BACKEND_URL}${url}` })} />
                    </div>
                    <button onClick={save} data-testid="gu-save" className="md:col-span-2 kind-btn-primary w-full justify-center"><Plus size={16} /> Add Guest</button>
                </div>
            </div>
            <div className="lg:col-span-7">
                <h3 className="font-serif text-2xl mb-6">All Guests ({items.length})</h3>
                <div className="grid md:grid-cols-2 gap-3">
                    {items.map((g) => (
                        <div key={g.id} className="flex gap-4 bg-white border border-black/10 p-4">
                            {g.photo_url && <img src={g.photo_url} alt={g.name} className="w-16 h-16 object-cover" />}
                            <div className="flex-1">
                                <div className="font-serif text-lg">{g.name}</div>
                                <div className="text-xs text-black/50">{g.title}</div>
                            </div>
                            <button onClick={() => del(g.id)} className="text-red-700"><Trash2 size={16} /></button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function TeamAdmin() {
    const [items, setItems] = useState([]);
    const empty = { name: "", role: "", bio: "", photo_url: "", quote: "", order: 0 };
    const [form, setForm] = useState(empty);
    const [editingId, setEditingId] = useState(null);
    const load = () => api.get("/team").then((r) => setItems(r.data || []));
    useEffect(() => { load(); }, []);

    const save = async () => {
        if (!form.name.trim() || !form.role.trim() || !form.bio.trim()) {
            toast.error("Name, role and bio are required");
            return;
        }
        try {
            if (editingId) {
                await api.put(`/team/${editingId}`, form);
                toast.success("Team member updated");
            } else {
                await api.post("/team", form);
                toast.success("Team member added");
            }
            setForm(empty);
            setEditingId(null);
            load();
        } catch {
            toast.error("Failed to save");
        }
    };
    const edit = (m) => {
        setEditingId(m.id);
        setForm({ name: m.name, role: m.role, bio: m.bio, photo_url: m.photo_url || "", quote: m.quote || "", order: m.order || 0 });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };
    const del = async (m) => {
        if (!window.confirm(`Delete ${m.name}? This cannot be undone.`)) return;
        await api.delete(`/team/${m.id}`);
        if (m.id === editingId) { setEditingId(null); setForm(empty); }
        load();
    };

    return (
        <div className="grid lg:grid-cols-12 gap-10" data-testid="admin-team">
            <div className="lg:col-span-5">
                <h3 className="font-serif text-2xl mb-6">{editingId ? "Edit Team Member" : "New Team Member"}</h3>
                <div className="grid md:grid-cols-2 gap-4 bg-white p-6 border border-black/10">
                    <TextField label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="tm-name" />
                    <TextField label="Role / Title" value={form.role} onChange={(v) => setForm({ ...form, role: v })} testid="tm-role" />
                    <div className="md:col-span-2">
                        <TextField label="Quote (optional)" value={form.quote} onChange={(v) => setForm({ ...form, quote: v })} testid="tm-quote" />
                    </div>
                    <TextArea label="Bio (blank line = new paragraph)" value={form.bio} onChange={(v) => setForm({ ...form, bio: v })} testid="tm-bio" />
                    <TextField label="Display Order" type="number" value={form.order} onChange={(v) => setForm({ ...form, order: v })} testid="tm-order" />
                    <div className="md:col-span-2">
                        <TextField label="Photo URL" value={form.photo_url} onChange={(v) => setForm({ ...form, photo_url: v })} testid="tm-photo" />
                        <FileUpload accept="image/*" label="Upload photo" onUpload={(url) => setForm({ ...form, photo_url: `${BACKEND_URL}${url}` })} />
                    </div>
                    <button onClick={save} data-testid="tm-save" className="md:col-span-2 kind-btn-primary w-full justify-center">
                        <Plus size={16} /> {editingId ? "Save Changes" : "Add Team Member"}
                    </button>
                    {editingId && (
                        <button
                            onClick={() => { setEditingId(null); setForm(empty); }}
                            data-testid="tm-cancel-edit"
                            className="md:col-span-2 text-sm text-black/50 hover:text-black underline"
                        >
                            Cancel editing
                        </button>
                    )}
                </div>
            </div>
            <div className="lg:col-span-7">
                <h3 className="font-serif text-2xl mb-6">Team ({items.length})</h3>
                <div className="space-y-3">
                    {items.map((m) => (
                        <div key={m.id} className="flex gap-4 bg-white border border-black/10 p-4 items-center">
                            <div className="w-16 h-16 bg-[#0A0A0A] flex-shrink-0 flex items-center justify-center">
                                {m.photo_url ? (
                                    <img src={m.photo_url} alt={m.name} className="w-full h-full object-cover" />
                                ) : (
                                    <span className="font-serif text-[#D4AF37] text-xl">{m.name?.[0]}</span>
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-serif text-lg truncate">{m.name}</div>
                                <div className="text-xs text-black/50 truncate">{m.role}</div>
                            </div>
                            <button onClick={() => edit(m)} data-testid={`tm-edit-${m.slug}`} className="eyebrow text-[10px] text-[#B8860B] hover:text-black flex-shrink-0">
                                Edit
                            </button>
                            <button onClick={() => del(m)} data-testid={`tm-delete-${m.slug}`} className="text-red-700 flex-shrink-0"><Trash2 size={16} /></button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function ProductsAdmin() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const load = async () => {
        try {
            const r = await api.get("/products");
            setItems(r.data || []);
        } catch {
            toast.error("Could not load Stripe products");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const refresh = async () => {
        setRefreshing(true);
        try {
            const r = await api.post("/products/refresh");
            setItems(r.data.products || []);
            toast.success(`Synced ${r.data.count} product(s) from Stripe`);
        } catch {
            toast.error("Refresh failed");
        } finally {
            setRefreshing(false);
        }
    };

    return (
        <div data-testid="admin-products">
            <div className="bg-white border border-black/10 p-6 mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <div className="eyebrow text-[#B8860B] mb-2 text-[10px]">Source of Truth</div>
                    <h3 className="font-serif text-2xl">Products live in your Stripe Dashboard</h3>
                    <p className="text-sm text-black/60 mt-2 max-w-2xl font-light">
                        Add, edit, archive or price your merch in Stripe. The shop reflects active products with a default one-time price. Use the <code className="bg-black/5 px-1 py-0.5 text-xs">stock</code> or <code className="bg-black/5 px-1 py-0.5 text-xs">category</code> metadata keys on a Stripe product to customize listings.
                    </p>
                </div>
                <div className="flex gap-3">
                    <button onClick={refresh} disabled={refreshing} data-testid="products-refresh" className="kind-btn-secondary">
                        {refreshing ? "Syncing…" : "Refresh from Stripe"}
                    </button>
                    <a href="https://dashboard.stripe.com/products" target="_blank" rel="noreferrer" data-testid="open-stripe-products" className="kind-btn-gold">
                        Open Stripe →
                    </a>
                </div>
            </div>

            <h3 className="font-serif text-2xl mb-6">Active Products ({items.length})</h3>
            {loading ? (
                <p className="text-black/50">Loading…</p>
            ) : items.length === 0 ? (
                <div className="bg-white border border-black/10 p-10 text-center">
                    <p className="font-serif text-xl mb-2">No active products yet.</p>
                    <p className="text-sm text-black/60">Add your first product in the Stripe Dashboard with a one-time price.</p>
                </div>
            ) : (
                <div className="grid md:grid-cols-2 gap-3">
                    {items.map((p) => (
                        <div key={p.price_id} className="flex gap-4 bg-white border border-black/10 p-4">
                            <div className="w-16 h-16 bg-[#F4F4F4] flex-shrink-0">
                                {p.image_url ? (
                                    <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-[#B8860B] font-serif text-xl">{p.name?.[0]}</div>
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-serif text-lg truncate">{p.name}</div>
                                <div className="text-xs text-black/50 tabular-nums">${p.price?.toFixed(2)} {p.currency?.toUpperCase()}</div>
                                <div className="text-[10px] text-black/40 truncate mt-1">{p.price_id}</div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function ApplicationsAdmin() {
    const [items, setItems] = useState([]);
    useEffect(() => { api.get("/guest-applications").then((r) => setItems(r.data || [])); }, []);
    return (
        <div>
            <h3 className="font-serif text-2xl mb-6">Guest Applications ({items.length})</h3>
            <div className="space-y-3">
                {items.length === 0 && <p className="text-black/50">No applications yet.</p>}
                {items.map((a) => (
                    <div key={a.id} className="bg-white border border-black/10 p-6">
                        <div className="flex justify-between mb-2">
                            <div className="font-serif text-xl">{a.name}</div>
                            <div className="text-xs text-black/50">{new Date(a.created_at).toLocaleDateString()}</div>
                        </div>
                        <div className="text-sm text-black/60 mb-2">{a.email} · {a.social_links}</div>
                        <div className="eyebrow text-[#B8860B] text-[10px] mb-2">Topic</div>
                        <p className="text-sm mb-3">{a.topic}</p>
                        <div className="eyebrow text-[#B8860B] text-[10px] mb-2">Bio</div>
                        <p className="text-sm text-black/70 font-light">{a.bio}</p>
                    </div>
                ))}
            </div>
        </div>
    );
}

function OrdersAdmin() {
    const [items, setItems] = useState([]);
    useEffect(() => { api.get("/orders").then((r) => setItems(r.data || [])); }, []);
    return (
        <div>
            <h3 className="font-serif text-2xl mb-6">Orders ({items.length})</h3>
            <div className="space-y-3">
                {items.length === 0 && <p className="text-black/50">No orders yet.</p>}
                {items.map((o) => (
                    <div key={o.id} className="bg-white border border-black/10 p-4 flex justify-between">
                        <div>
                            <div className="font-serif text-lg">${o.amount?.toFixed(2)} · {(o.items || []).length} item(s)</div>
                            <div className="text-xs text-black/50">{o.customer_email || "—"} · {new Date(o.created_at).toLocaleString()}</div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function DonationsAdmin() {
    const [items, setItems] = useState([]);
    useEffect(() => { api.get("/donations").then((r) => setItems(r.data || [])); }, []);
    const total = items.reduce((s, d) => s + (d.amount || 0), 0);
    return (
        <div data-testid="admin-donations">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
                <h3 className="font-serif text-2xl">Donations ({items.length})</h3>
                <div data-testid="admin-donations-total" className="font-serif text-2xl text-[#B8860B] tabular-nums">
                    ${total.toLocaleString(undefined, { minimumFractionDigits: 2 })} raised
                </div>
            </div>
            <div className="bg-white border border-black/10">
                <div className="grid grid-cols-12 px-6 py-3 eyebrow text-[10px] text-black/50 border-b border-black/10">
                    <span className="col-span-4">Donor</span>
                    <span className="col-span-4">Email</span>
                    <span className="col-span-2 text-right">Amount</span>
                    <span className="col-span-2 text-right">Date</span>
                </div>
                {items.length === 0 && <div className="p-6 text-black/50">No donations yet.</div>}
                {items.map((d) => (
                    <div key={d.id} data-testid={`donation-row-${d.id}`} className="grid grid-cols-12 px-6 py-3 text-sm border-b border-black/5 last:border-b-0 items-center">
                        <span className="col-span-4 font-medium truncate">{d.donor_name || <span className="text-black/40 italic font-normal">Anonymous</span>}</span>
                        <span className="col-span-4 text-black/60 truncate">{d.donor_email || "—"}</span>
                        <span className="col-span-2 text-right tabular-nums">${d.amount?.toFixed(2)}</span>
                        <span className="col-span-2 text-right text-black/50">{new Date(d.created_at).toLocaleDateString()}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function NewsletterAdmin() {
    const [items, setItems] = useState([]);
    useEffect(() => { api.get("/newsletter").then((r) => setItems(r.data || [])); }, []);
    return (
        <div>
            <h3 className="font-serif text-2xl mb-6">Newsletter Subscribers ({items.length})</h3>
            <div className="bg-white border border-black/10 divide-y divide-black/10">
                {items.length === 0 && <div className="p-6 text-black/50">No subscribers yet.</div>}
                {items.map((s) => (
                    <div key={s.id} className="px-6 py-3 flex justify-between text-sm">
                        <span>{s.email}</span>
                        <span className="text-black/50">{new Date(s.created_at).toLocaleDateString()}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}
